import { useState, useEffect, useRef } from 'react';
import { Identifier, useDataProvider, useNotify } from 'react-admin';
import { SubdivisionBuilderSupervisorDto } from '@vatos-pas/common';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_ADD_REPAIR_CONTRACTOR,
  CAN_CREATE_REPAIR,
  CAN_MANAGE_HO_SERVICE_REPAIR,
  CAN_SEND_EMAIL_CLIENT,
} from 'providers/permissions';
import { Repair } from '../../types';
import type { Job } from '../../containers/useMasterSheetContainer';
import { sendMail as sendMailMultiSupervisor } from '../../utils/emailManagementDrywall';
import { sendMail as sendMailSingleSupervisor } from '../../utils/emailManagement';
import { getAddContractorsBlocked } from '../../utils/getAddContractorsBlocked';
import { useWorkflowConfig } from '../../config';
import { canEditHOServiceRepair } from 'modules/master-sheet-v2/utils/canEditHOServiceRepair';

interface UseRepairsLogicProps {
  theJob: any;
  refreshRepairs: () => Promise<any>;
  initialValue?: string;
  setJob: any;
}

export const useRepairsLogic = ({
  theJob,
  refreshRepairs,
  initialValue,
  setJob,
}: UseRepairsLogicProps) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { hasPermission } = PermissionsProvider.useContainer();
  const config = useWorkflowConfig();

  const [poReleasedInfoOpen, setPoReleasedInfoOpen] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [showAddRepairModal, setShowAddRepairModal] = useState(false);
  const [repair, setRepair] = useState<Repair>();
  const [openCheckPhotosModal, setOpenCheckPhotosModal] = useState(false);
  const [isShowView, setIsShowView] = useState(false);
  const [title, setTitle] = useState('');
  const [photos, setPhotos] = useState<any>([]);
  const [loadingBuilderSupervisorEmail, setLoadingBuilderSupervisorEmail] =
    useState(false);
  const [hasPhotos, setHasPhotos] = useState(false);
  const [emailManagementOpen, setEmailManagementOpen] = useState(false);
  const [emailRepairId, setEmailRepairId] = useState<Identifier | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const [subdivisionBuilderSupervisors, setSubdivisionBuilderSupervisors] =
    useState<SubdivisionBuilderSupervisorDto[] | null>(null);

  const canSendEmailClient = hasPermission(CAN_SEND_EMAIL_CLIENT);
  const canCreateRepair = hasPermission(CAN_CREATE_REPAIR);
  const canAddContractor = hasPermission(CAN_ADD_REPAIR_CONTRACTOR);
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);

  const repairs = theJob?.jobRepairs || [];
  const hasRepairs = !!repairs && repairs.length > 0;

  const handleSendMail = async (repairId: Identifier | undefined | null) => {
    if (!repairId) return;

    const sendMail =
      config.email.managementType === 'multi-supervisor'
        ? sendMailMultiSupervisor
        : sendMailSingleSupervisor;

    try {
      setLoadingBuilderSupervisorEmail(true);
      await sendMail(repairId);
    } catch (error: any) {
      notify(error.message, 'warning');
    } finally {
      setLoadingBuilderSupervisorEmail(false);
    }
  };

  const getSubdivisionBuilderSupervisors = async (subdivisionId: string) => {
    const builderSupervisors =
      await dataProvider.getList<SubdivisionBuilderSupervisorDto>(
        'subdivision-builder-supervisor',
        {
          filter: { subdivisionId },
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'user.firstName', order: 'ASC' },
        },
      );

    if (builderSupervisors?.data.length) {
      setSubdivisionBuilderSupervisors(builderSupervisors.data);
    }
  };

  /** The repair-create endpoint records lot activity before returning success. */
  const onAddRepair = async (repair: Repair, hasPhotos: boolean) => {
    const jobId = theJob.job.id;
    setJob((current: Job | null) =>
      current && current.job.id === jobId
        ? { ...current, job: { ...current.job, archived: false } }
        : current,
    );
    const refreshedRepairs = await refreshRepairs();

    const createdRepair = refreshedRepairs?.find(
      record => record.id === repair?.id,
    );

    if (!createdRepair) return;

    setRepair(createdRepair);
    setShowRepairModal(true);

    if (canSendEmailClient) {
      setEmailRepairId(createdRepair.id);
      await handleSendMail(createdRepair.id);
      setHasPhotos(hasPhotos);
      setEmailManagementOpen(true);
    }
  };

  const handleUploadClick = async (repair: any) => {
    const jobRepairPhotos = await dataProvider.getList<any>(
      'job-repair-photo',
      {
        filter: { jobRepairId: repair.id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'ASC' },
      },
    );

    if (jobRepairPhotos.data) {
      setTitle('Photos');
      setPhotos(jobRepairPhotos.data);
      setOpenCheckPhotosModal(true);
    }
  };

  const getRepairButtonText = (repair: any) => {
    const canEdit = canAddContractor && canEditHOServiceRepair(repair?.repairType, canManageHOService);
    const isAddContractorsBlocked = getAddContractorsBlocked(repair);

    return canEdit
      ? isAddContractorsBlocked
        ? 'Review Details'
        : 'Add Contractor'
      : 'Show Contractors';
  };

  const handleRepairActionClick = (repair: any) => {
    const canEdit = canAddContractor && canEditHOServiceRepair(repair?.repairType, canManageHOService);
    setRepair(repair);
    setIsShowView(!canEdit);
    setShowRepairModal(true);
  };

  useEffect(() => {
    const openInitialRepair = () => {
      if (!initialValue) return;
      scroll.current?.scrollIntoView();
    };
    if (theJob?.job?.subdivisionId) {
      getSubdivisionBuilderSupervisors(theJob.job.subdivisionId);
    }
    openInitialRepair();
  }, [initialValue, theJob?.job?.subdivisionId]);

  return {
    // States
    repairs,
    hasRepairs,
    poReleasedInfoOpen,
    setPoReleasedInfoOpen,
    showRepairModal,
    setShowRepairModal,
    showAddRepairModal,
    setShowAddRepairModal,
    repair,
    setRepair,
    openCheckPhotosModal,
    setOpenCheckPhotosModal,
    isShowView,
    title,
    photos,
    loadingBuilderSupervisorEmail,
    hasPhotos,
    setHasPhotos,
    emailManagementOpen,
    setEmailManagementOpen,
    emailRepairId,
    setEmailRepairId,
    scroll,
    subdivisionBuilderSupervisors,

    // Permissions
    canSendEmailClient,
    canCreateRepair,
    canAddContractor,

    // Handlers
    handleSendMail,
    onAddRepair,
    handleUploadClick,
    getRepairButtonText,
    handleRepairActionClick,

    // Props passed through
    theJob,
    refreshRepairs,
    setJob,
  };
};
